import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { hasPermission } from "hooks/usePermission";
import CustomButton from "components/common/Button";
import CustomChip from "components/common/Chip";
import CustomTooltip from "components/common/Tooltip";
import SectionLoader from "components/common/SectionLoader";
import TableComponent from "components/common/TableComponent";
import { AlertType } from "components/common/Snackbar";

import { GetRoles, GetCatalog } from "store/actions/Rbac";
import { showSnackbar } from "store/actions/Utility";

import { createRole, updateRole, deleteRole } from "services/rbac";

import RoleDialog from "./RoleDialog";
import "./RoleManagement.scss";

export default function RoleManagement() {
  const dispatch = useDispatch();
  const { roles, catalog, loadingRoles } = useSelector((s) => s.Rbac || {});
  const [dialog, setDialog] = useState(null); // { mode: "create"|"edit", role } | null

  const canCreateRole = hasPermission("roles.create");
  const canUpdateRole = hasPermission("roles.update");
  const canDeleteRole = hasPermission("roles.delete");

  const reload = () => {
    dispatch(GetRoles());
    dispatch(GetCatalog());
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (payload) => {
    try {
      const { mode, roleId, name, userType, description, permissionKeys } = payload;
      const res =
        mode === "create"
          ? await createRole({ name, userType, description, permissionKeys })
          : await updateRole(roleId, { name, description, permissionKeys });

      if (res && res.ok) {
        dispatch(showSnackbar({ type: AlertType.success, message: "Saved", dispatch }));
        setDialog(null);
        reload();
      } else {
        dispatch(
          showSnackbar({ type: AlertType.error, message: res?.payload?.message || "Action failed", dispatch })
        );
      }
    } catch (e) {
      dispatch(showSnackbar({ type: AlertType.error, message: "Action failed", dispatch }));
    }
  };

  const handleDelete = async (role) => {
    try {
      const res = await deleteRole(role.Role_ID);
      if (res && res.ok) {
        dispatch(showSnackbar({ type: AlertType.success, message: "Deleted", dispatch }));
        reload();
      } else {
        dispatch(
          showSnackbar({
            type: AlertType.error,
            message: res?.payload?.message || "Cannot delete this role",
            dispatch,
          })
        );
      }
    } catch (e) {
      dispatch(showSnackbar({ type: AlertType.error, message: "Cannot delete this role", dispatch }));
    }
  };

  const columns = [
    { id: "Name", label: "Name" },
    { id: "User_Type", label: "User type" },
    {
      id: "Type",
      label: "Type",
      render: (value, row) =>
        row.Is_System ? (
          <CustomChip label="System" size="small" />
        ) : (
          <CustomChip label="Custom" size="small" variant="outlined" />
        ),
    },
    {
      id: "action",
      label: "Actions",
      align: "right",
      // System roles are read-only: hide edit/delete (mirrors UserItem).
      // Each control is additionally gated on its roles.* permission.
      render: (value, row) =>
        row.Is_System ? null : (
          <>
            {canUpdateRole && (
              <CustomTooltip content="Edit">
                <IconButton
                  aria-label="Edit role"
                  size="small"
                  className="role-management__action"
                  onClick={() => setDialog({ mode: "edit", role: row })}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </CustomTooltip>
            )}
            {canDeleteRole && (
              <CustomTooltip content="Delete">
                <IconButton
                  aria-label="Delete role"
                  size="small"
                  className="role-management__action role-management__action--danger"
                  onClick={() => handleDelete(row)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CustomTooltip>
            )}
          </>
        ),
    },
  ];

  return (
    <div className="role-management">
      <div className="role-management__header">
        <div className="heading-h3-semibold">Roles</div>
        {canCreateRole && (
          <CustomButton
            label="Add role"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setDialog({ mode: "create", role: null })}
          />
        )}
      </div>

      {loadingRoles ? (
        <SectionLoader />
      ) : (
        <TableComponent columns={columns} data={roles || []} disableSort tableCellSmall tableHeadSmall />
      )}

      <RoleDialog
        open={!!dialog}
        mode={dialog?.mode}
        role={dialog?.role}
        catalog={catalog || []}
        onClose={() => setDialog(null)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
